import React from 'react';
import capitalize from 'lodash/capitalize';
import Lozenge from '@atlaskit/lozenge';
import { getChargeElement, pluralize } from '@css-xp/ppc-utils';
import { EntitlementIcon, FormattedDate, SandboxLozenge, TrialLozenge, UpgradePremiumTrial } from '@css-xp/base';
import { NoData } from '@css-xp/ppc-common';
import { ROVO_PRODUCT_ID } from '@css-xp/ppc-constants';
import { ProductTrialInfo } from './product-trial-info-lozenge';
import { OrderSummaryLineItems } from './types';
import type { State } from '../../../../store/types';
import { ProductStatusLozenge, CustomPanel, Estimate } from '@css-xp/ppc-purchase-experience';
import { getPlanChangeContent } from '@css-xp/ppc-purchase-experience/src/utils/uof-utils/get-plan-change-content';
import { ProductStatus } from '../../../../types';
import { IntegratedAppsCustomPanel } from './integrated-apps-custom-panel';
import './line-items.scss';

export type LineItemsProps = {
  lineItems: OrderSummaryLineItems;
  nextBillDate: State['nextBillDate'];
  isJiraFamily?: boolean;
  loading: boolean;
  showSandboxStatus?: boolean;
  selectedCurrency?: string;
  isTWCPhase2Enabled?: boolean;
};

export const LineItemsCustomPanel: React.FC<LineItemsProps> = ({
  lineItems,
  loading,
  nextBillDate,
  showSandboxStatus,
  selectedCurrency = 'USD',
}) => {
  return (
    <>
      {lineItems.map((lineItem) => {
        const productTrialLength = lineItem?.trialLengthInDays;
        const productStatuses = lineItem?.productStatuses;
        const hasLozengeItems = !!(
          lineItem?.trialLengthInDays ||
          productStatuses?.length ||
          lineItem?.otherLozengesOrStatuses ||
          lineItem?.inTrial ||
          (showSandboxStatus && lineItem?.isSandbox && lineItem.productKey !== ROVO_PRODUCT_ID)
        );

        const isAmendmentOrder = lineItem?.productStatuses?.some((productStatus) => {
          return productStatus === ProductStatus.EDITED;
        });

        const isTransferOrder = lineItem?.productStatuses?.some((productStatus) => {
          return productStatus === ProductStatus.TRANSFER;
        });

        const cartLevelCustomBillDate = nextBillDate.cartLevel;
        const cloudSiteLevelCustomBillDate = nextBillDate.cloudSiteLevel?.[lineItem.existingCloudSite ?? ''];

        const nextBillDateForLineItem =
          cartLevelCustomBillDate || cloudSiteLevelCustomBillDate || lineItem?.nextBillDate;
        return (
          <>
            <div className="line-item-app-container" key={`${lineItem.lineItemId}`}>
              <div className="line-item-app-container-icon">
                <EntitlementIcon productId={lineItem?.productKey} className="avatar medium pt-4" isCloud />
              </div>
              <CustomPanel
                testId={`line-item-app-container-${lineItem?.lineItemId}`}
                className="line-item-app-container-custom-panel"
                heading={
                  <>
                    <div className="line-item-app-container-selected-product-row-container">
                      <div className="existing-site-container">
                        <h4 className="line-item-app-container-selected-product-name" data-testid="selected-app-name">
                          {lineItem?.name}
                        </h4>
                        {hasLozengeItems && (
                          <div className="line-item-app-container-selected-product-flex line-item-app-container-lozenge">
                            {showSandboxStatus && lineItem?.isSandbox && lineItem.productKey !== ROVO_PRODUCT_ID && (
                              <div
                                className="line-item-product-in-trial"
                                data-testId={`line-item-product-sandbox-${lineItem?.lineItemId}`}
                              >
                                <SandboxLozenge />
                              </div>
                            )}
                            {productStatuses?.map((status) => {
                              return <ProductStatusLozenge key={`product-status-${status}`} status={status} />;
                            })}
                            {/* show trial lozenge only if the product is in trial */}
                            {lineItem?.inTrial && (
                              <>
                                <ProductTrialInfo trialLength={productTrialLength} />
                                {!productStatuses?.includes(ProductStatus.NEW) && (
                                  <div
                                    className="line-item-product-in-trial"
                                    data-testId={`line-item-product-in-trial-${lineItem?.lineItemId}`}
                                  >
                                    <TrialLozenge />
                                  </div>
                                )}
                              </>
                            )}
                            {lineItem?.isOnUpgradeTrial && (
                              <div data-testId={`line-item-product-in-upgrade-trial-${lineItem?.id}`}>
                                <UpgradePremiumTrial />
                              </div>
                            )}
                            {lineItem?.otherLozengesOrStatuses?.map((status: string) => {
                              return (
                                <Lozenge key={`other-lozenges-${status}`} appearance={'new'}>
                                  {capitalize(status)}
                                </Lozenge>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <Estimate
                        currency={selectedCurrency}
                        price={lineItem?.grandTotal ?? lineItem?.listPrice ?? 0}
                        loading={loading}
                        className={'line-item-app-container-selected-product-estimated-price'}
                      />
                    </div>
                  </>
                }
              >
                <div className="line-item-app-container-selected-product">
                  <div className="line-item-app-container-selected-product-row-container">
                    <p className="line-item-app-container-selected-product-pricing-details">List price</p>
                    <Estimate
                      currency={selectedCurrency}
                      price={lineItem?.listPrice ?? 0}
                      loading={loading}
                      className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                    />
                  </div>
                  <div className="line-item-app-container-selected-product-row-container">
                    <p className="line-item-app-container-selected-product-pricing-details">{`Partner discount: ${lineItem?.discountPercent}%`}</p>
                    <Estimate
                      currency={selectedCurrency}
                      price={-(lineItem?.totalDiscount ?? 0)}
                      loading={loading}
                      className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                    />
                  </div>
                  <div className="line-item-app-container-selected-product-row-container">
                    <h4 className="line-item-app-container-selected-product-estimate-partner-discount">
                      Estimate incl. discounts
                    </h4>
                    <Estimate
                      currency={selectedCurrency}
                      price={lineItem?.estimateIncludingPartnerDiscount ?? 0}
                      loading={loading}
                      className={'line-item-app-container-selected-product-estimate-partner-discount-currency'}
                    />
                  </div>
                  <div className="line-item-app-container-selected-product-info-row-container">
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-info-pricing-details">
                        Entitlement number: {lineItem?.entitlementNumber || <NoData />}
                      </p>
                    </div>
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-info-pricing-details">
                        {`Plan: ${lineItem?.offeringName}`}
                        {!lineItem?.isFreeProduct ? ' | ' + capitalize(lineItem?.billingCycleLabel) : ''}
                        {lineItem?.inTrial && productTrialLength
                          ? ` | ${pluralize(Number(productTrialLength), 'Day')} trial`
                          : ''}
                      </p>
                    </div>
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-info-pricing-details">
                        Next bill date:{' '}
                        {nextBillDateForLineItem ? <FormattedDate date={nextBillDateForLineItem} /> : <NoData />}
                      </p>
                    </div>
                    {lineItem?.startDate !== undefined && (
                      <div className="line-item-app-container-selected-product-row-container">
                        <p className="line-item-app-container-selected-product-info-pricing-details">
                          Start date: {lineItem.startDate}
                        </p>
                      </div>
                    )}
                    {lineItem?.renewalOrEndDate !== undefined && (
                      <div className="line-item-app-container-selected-product-row-container">
                        <p className="line-item-app-container-selected-product-info-pricing-details">
                          Renewal / End date: {lineItem.renewalOrEndDate}
                        </p>
                      </div>
                    )}
                    {!lineItem.isFreeProduct && (
                      <div className="line-item-app-container-selected-product-row-container">
                        <p className="line-item-app-container-selected-product-info-pricing-details">
                          Estimated {getChargeElement({ productId: lineItem?.productKey, labelPlural: true })}:{' '}
                          {lineItem?.estimatedNumberOfUsers}
                        </p>
                      </div>
                    )}
                  </div>
                  {lineItem?.isAdminFeeApplicable && (
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-pricing-details">{`Administration fee (DST)`}</p>
                      <Estimate
                        currency={selectedCurrency}
                        price={lineItem?.adminFee ?? 0}
                        loading={loading}
                        className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                      />
                    </div>
                  )}

                  {lineItem?.itemLevelTaxInfo?.map(([taxLabel, { tax, taxPercent }]) => {
                    return (
                      <div
                        key={`tax-(${taxPercent}-${taxLabel})`}
                        className="line-item-app-container-selected-product-row-container"
                      >
                        <p className="line-item-app-container-selected-product-pricing-details">{`Tax (${taxPercent}% ${taxLabel})`}</p>
                        <Estimate
                          currency={selectedCurrency}
                          price={tax}
                          loading={loading}
                          className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
                        />
                      </div>
                    );
                  })}
                  {isAmendmentOrder && (
                    <div className="line-item-app-container-selected-product-row-container">
                      <p className="line-item-app-container-selected-product-info-pricing-details">
                        {getPlanChangeContent({
                          initialOffering: lineItem?.initialOfferingName,
                          currentOffering: lineItem?.offeringName,
                          isInTrial: lineItem?.inTrial,
                          isItADowngrade: lineItem?.isItADowngrade,
                          isItAUpgrade: lineItem?.isItAUpgrade,
                          startDate: lineItem?.newPlanStartDate,
                          isATransferCase: isTransferOrder,
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </CustomPanel>
            </div>
            {lineItem?.appLineItems && lineItem?.appLineItems.length > 0 && (
              <IntegratedAppsCustomPanel
                appLineItems={lineItem.appLineItems}
                lineItemId={lineItem.lineItemId}
                totalPriceOfApps={lineItem?.totalPriceOfApps}
                loading={loading}
                showSandboxStatus={showSandboxStatus}
                selectedCurrency={selectedCurrency}
              />
            )}
          </>
        );
      })}
    </>
  );
};
